"use client";
import {
  CustomButton,
  FlexBetween,
  FlexBetweenCol,
  Logo,
  TextField,
} from "@/components";
import { usePostalCodeInquiryMutation } from "@/hooks/useInquiry";
import {
  IServiceFormProps,
  TPostalCode,
  TRawServiceApiKeys,
} from "@/interfaces";
import { cn } from "@/lib";
import { useAppStore, useModalStore, useSecureStore } from "@/store";
import { codePostiServiceInquirySchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import InquiryCost from "../../inquiryCost";
import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { noNavigateServices } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import HtmlRenderer from "../../htmlRenderer";
import MainLogo from "@public/svg/logo-2.svg";
import Loader from "@/components/ui/loader";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { IPostalCodeInquiryMutationPayload } from "@/interfaces/inquiries/governmental";
import SampleInquiryModal from "@/components/inquirySamples/sampleInquiryModal";
import PostalCodeSample from "@/components/inquirySamples/postalCodeSample";

const PostalCodeInquiryForm = ({
  cost,
  isLoggedIn,
  navigateToLogin,
  announcement,
  disableService,
  serviceId,
}: IServiceFormProps) => {
  const { data: session } = useSession();
  const { persistedFields } = useSecureStore();
  const { setPostLoginAction } = useAppStore();
  const {
    isPending: isSubmitting,
    mutate: postalCodeInquiryFn,
    error: postalCodeInquiryError,
    reset: resetPostalCodeInquiry,
  } = usePostalCodeInquiryMutation(session?.user.token);
  const router = useRouter();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const isPaymentMethodModalOpen = openModal === "payment-method";
  const openPaymentModal = () => {
    setOpenModal("payment-method");
  };

  const serviceKey = TRawServiceApiKeys.Postal_Code_Inquiry;
  const [packageId, setPackageId] = useState<string | null>(null);
  // Handle package selection
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
  } = useForm<TPostalCode>({
    resolver: zodResolver(codePostiServiceInquirySchema),
    defaultValues: {
      digit: persistedFields?.[serviceKey]?.digit || "",
    },
  });

  const t = useTranslations("serviceFormSubmissions");

  const pathname = usePathname();

  const activeKey = noNavigateServices.find(
    (item) => item.key === globalRawServicesData.Postal_Code_Inquiry.key,
  )!;

  const searchParams = useSearchParams();
  useEffect(() => {
    if (searchParams?.get("payment") === "1") {
      setOpenModal("payment-method");
    }
  }, [searchParams, setOpenModal]);

  const [isSampleOpen, setIsSampleOpen] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (data: any) => {
    setError(null);
    if (!isLoggedIn) {
      setPostLoginAction("open-payment-modal");
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  const paymentSubmission = () => {
    const formData = getValues();

    const payload: Readonly<IPostalCodeInquiryMutationPayload> = {
      code: formData.digit,
      campaign: packageId || undefined,
    };
    postalCodeInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };

  useEffect(() => {
    if (isPaymentMethodModalOpen) {
      resetPostalCodeInquiry();
    }
  }, [isPaymentMethodModalOpen]);

  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexBetweenCol gap={8} className={cn("w-full")}>
          <div className="mb-8 w-full max-w-[80%]">
            <Controller
              name={"digit"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  maxLength={10}
                  aria-label="شماره پستی"
                  numeric
                  hasShadow
                  inputMode="numeric"
                  placeholder={t("postalCodeInquiry.placeholder")}
                  inputClassName={cn("tracking-widest h-10")}
                  error={formError.digit?.message}
                />
              )}
            />
          </div>
          {session?.user.token && <InquiryCost cost={cost} />}
          {announcement && <HtmlRenderer htmlString={announcement} />}
          <FlexBetween gap={2} className="lg:gap-4">
            {/*<CustomButton*/}
            {/*  id="postal-code-inquiry-sample"*/}
            {/*  type="button"*/}
            {/*  className="border-primary-main text-primary-main hover:bg-primary-main/5 border bg-transparent !px-3 !text-sm"*/}
            {/*  onClick={() => setIsSampleOpen(true)}*/}
            {/*>*/}
            {/*  نمونه نتیجه*/}
            {/*</CustomButton>*/}
            <CustomButton
              id="postal-code-inquiry-submit"
              disabled={disableService}
              className="flex-1"
              variant="primary"
              type="submit"
            >
              {t("postalCodeInquiry.confirmButton")}
            </CustomButton>
          </FlexBetween>
        </FlexBetweenCol>
      </form>
      {isSampleOpen && (
        <SampleInquiryModal
          onClose={() => setIsSampleOpen(false)}
          title="نمونه نتیجه استعلام کد پستی"
        >
          <PostalCodeSample />
        </SampleInquiryModal>
      )}
      {isPaymentMethodModalOpen && (
        <PaymentModalContainer
          payBtnId="payment-postal-code-inquiry"
          submissionError={postalCodeInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isPaymentMethodModalOpen}
          onClose={closeModal}
          selectedService={activeKey}
          submissionLoading={isSubmitting}
          onPackageSelect={handlePackageSelect}
          onErrorReset={resetPostalCodeInquiry}
          serviceId={serviceId}
          showData="service"
          persistedData={{
            [serviceKey]: {
              ...getValues(),
            },
          }}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
      {isSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <Logo className="mb-3" src={MainLogo} width={60} height={60} />
            <h2 className="text-center text-sm font-semibold sm:text-base">
              درحال استعلام <br /> لطفا شکیبا باشید
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostalCodeInquiryForm;
